<template>
  <BaseLayout :heading="'Clock Drawing'" />

  <div class="clock-drawing-container">
    <div class="image-container">
      <div v-if="loading" class="loading-message">
        Loading clock drawing image...
      </div>
      <div v-else-if="error" class="error-message">
        {{ error }}
      </div>
      <div v-else-if="imageUrl" class="clock-image-container">
        <img :src="imageUrl" alt="Clock Drawing" class="clock-drawing-image" />
      </div>
      <div v-else class="no-image-message">
        No clock drawing image found for this participant.
      </div>
    </div>

    <div v-if="isScoring">
      <ClockDrawingScoring />
    </div>
    <div v-else>
      <ClockDrawingAdminister />
    </div>
  </div>

  <NotesSection 
    :administeringNotes="jsonData ? jsonData.administeringNotes : ''" 
    :scoringNotes="jsonData ? jsonData.scoringNotes : ''"
    :clockDrawingScore="jsonData ? jsonData.clockDrawingScore : ''"
    :isClockDrawing="true"
    :visitId="visitId"
    :taskType="'Clock Drawing'"
    ref="notesSection" />

  <TextButton buttonText="Submit" :dark="true" type="submit" @click="submitForm" />
  
  <NotificationPopup 
    :message="notificationMessage" 
    :visible="notificationVisible"
  />

  <ConfirmationPopup
    :visible="showConfirmPopup"
    @confirm="handleConfirmLeave"
    @noSave="handleNoSaveLeave"
    @cancel="showConfirmPopup = false"
  />
</template>

<script>
import BaseLayout from './BaseLayout.vue';
import NotesSection from '../components/NotesSection.vue';
import ClockDrawingAdminister from '../components/Pages/ClockDrawingAdminister.vue';
import ClockDrawingScoring from '../components/Pages/ClockDrawingScoring.vue';
import TaskController from '../controllers/TaskController.js';
import ServerCodeModel from '../models/ServerCodeModel';
import TextButton from '@/components/TextButton.vue';
import NotificationPopup from '@/components/NotificationPopup.vue';
import ConfirmationPopup from '@/components/ConfirmationPopup.vue';
import ACTApiService from '@/services/ACTApiService.js';

export default {
  components: {
      BaseLayout,
      NotesSection,
      ClockDrawingAdminister,
      ClockDrawingScoring,
      TextButton,
      NotificationPopup,
      ConfirmationPopup
  },
  computed: {
    isScoring() {
        return this.$route.query.selection === 'score';
    },
    isAdministering() {
      return this.$route.query.selection !== 'score';
    }
  },
  data() {
    return {
      controller: null,
      jsonData: null,
      notificationMessage: '',
      notificationVisible: false,
      showConfirmPopup: false,
      nextRoute: null,
      forceNavigate: false,
      imageUrl: null,
      loading: true,
      error: null,
      actApiService: new ACTApiService(),
      visitId: localStorage.getItem('visitId')
    };
  },
  async mounted() {
    const serverCodeModel = new ServerCodeModel();
    serverCodeModel.logType = 'InterviewerClockDrawingLog';

    this.controller = new TaskController(serverCodeModel.logType, 'clockDrawingData', serverCodeModel);

    // Update visitId from localStorage
    this.visitId = localStorage.getItem('visitId');
    console.log('ClockDrawing mounted, visitId:', this.visitId);

    await this.loadData();
    await this.fetchClockDrawingImage();
  },
  methods: {
    async loadData() {
      await this.controller.loadData();
      this.jsonData = this.controller.model.data;
    },
    async fetchClockDrawingImage() {
      this.loading = true;
      this.error = null;
      
      const logId = localStorage.getItem('clockDrawingLogId');
      
      if (!logId) {
        this.loading = false;
        return; // No image to fetch
      }
      
      try {
        const imageBlob = await this.actApiService.getLogBinary(logId);
        this.imageUrl = URL.createObjectURL(imageBlob);
      } catch (error) {
        console.error('Error fetching clock drawing image:', error);
        this.error = 'Failed to load clock drawing image. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    async submitForm() {
      this.notificationMessage = 'Submitting...';
      this.notificationVisible = true;

      // Set timestamps
      if (this.jsonData.timeCreated === '') {
        this.jsonData.timeCreated = new Date().toUTCString();
      }
      this.jsonData.timeLastModified = new Date().toUTCString();

      // Get notes from NotesSection component
      const notesSection = this.$refs.notesSection;
      
      if (this.jsonData) {
        // Always update the notes that correspond to the current mode
        if (this.isAdministering) {
          // In admin mode, only update administering notes
          const administeringNotes = notesSection.getAdministeringNotes();
          this.jsonData.administeringNotes = administeringNotes || '';
        } else {
          // In scoring mode, update scoring notes and clock drawing score
          const scoringNotes = notesSection.getScoringNotes();
          const clockDrawingScore = notesSection.getClockDrawingScore();
          this.jsonData.scoringNotes = scoringNotes || '';
          this.jsonData.clockDrawingScore = clockDrawingScore || '';
        }
      }

      // Explicitly save data to model before submitting
      await this.controller.model.saveData(this.jsonData);
      
      // Also save to localStorage directly to ensure it's there
      const subjectId = localStorage.getItem('subjectId');
      if (subjectId) {
        const allData = JSON.parse(localStorage.getItem('subjectData') || '{}');
        if (!allData[subjectId]) {
          allData[subjectId] = {};
        }
        
        // Save under clockDrawingData key
        allData[subjectId].clockDrawingData = this.jsonData;
        
        localStorage.setItem('subjectData', JSON.stringify(allData));
        
        console.log('Saved Clock Drawing data:', this.jsonData);
      }

      try {
        await this.controller.submitForm();
        this.notificationMessage = 'Submitted Successfully!';
        this.hasUnsavedChanges = false;
      } catch (error) {
        this.notificationMessage = 'Submitting failed. Please try again.';
        console.error('Error submitting form:', error);
      }
      finally {
        this.resetNotification();
      }
    },
    resetNotification() {
      setTimeout(() => {
        this.notificationVisible = false;
      }, 3000);
    },
    async handleConfirmLeave() {
      this.showConfirmPopup = false;
      await this.submitForm();
      this.$router.push(this.nextRoute);
    },
    handleNoSaveLeave() {
      this.showConfirmPopup = false;
      this.forceNavigate = true;
      this.$router.push(this.nextRoute);
    }
  },
  beforeRouteLeave(to, from, next) {
    const notesSection = this.$refs.notesSection;
    const currentAdministeringNotes = notesSection.getAdministeringNotes();
    const currentScoringNotes = notesSection.getScoringNotes();

    if (this.forceNavigate) {
      this.forceNavigate = false;
      next();
      return;
    }

    if ( 
      (!this.isScoring && currentAdministeringNotes !== this.jsonData.administeringNotes) || 
      (this.isScoring && currentScoringNotes !== this.jsonData.scoringNotes) 
    ) {
      this.showConfirmPopup = true;
      this.nextRoute = to.fullPath;
      next(false);
    } else {
      this.controller.model.saveData(this.jsonData);
      next();
    }
  },
  beforeUnmount() {
    // Clean up object URL to avoid memory leaks
    if (this.imageUrl) {
      URL.revokeObjectURL(this.imageUrl);
    }
  }
};
</script>

<style scoped>
.clock-drawing-container {
  margin-bottom: 20px;
}

.image-container {
  margin-bottom: 20px;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ddd;
  padding: 10px;
  border-radius: 5px;
  background-color: #f9f9f9;
}

.clock-drawing-image {
  max-width: 100%;
  max-height: 400px;
  object-fit: contain;
}

.loading-message, .error-message, .no-image-message {
  padding: 20px;
  text-align: center;
  font-size: 16px;
}

.error-message {
  color: #d9534f;
}

.no-image-message {
  color: #666;
  font-style: italic;
}

.clock-image-container {
  display: flex;
  justify-content: center;
  width: 100%;
}
</style>
